import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faGear as ConfigurationIcon,
  faTrashCan as DeleteIcon,
  faDownload as DownloadIcon,
  faCircleExclamation as FeedRuleIcon,
  faFile as FileIcon,
  faGavel as RuleIcon
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { startCase } from 'lodash';
import type { UseFormClearErrors, UseFormSetError } from 'react-hook-form';

import ActionWrapper from '@/components/ActionWrapper';
import CheckPermissions from '@/components/CheckPermissions';
import RulesLevelChip from '@/components/RulesLevelChip';
import { PERMISSIONS } from '@/constants/auth.constants';
import type RulesDialog from '@/features/RulesCreateDialog/RulesCreateDialog.types';
import {
  type AllSensorRuleFile,
  rulesDeleteDialogAtom,
  rulesDeleteDialogOpenAtom,
  type SensorRuleLevelDTO
} from '@/pages/Administration/AdministrationRules';
import { axiosClient } from '@/services/BaseApi.service';
import getDateTime from '@/utils/getDateTime.utils';

const handleDownloadClick = (row: AllSensorRuleFile) => {
  const downloadSensorRule = async () => {
    const response = await axiosClient.get(
      `/organization_settings/sensor_rules/${row.organization_label}/file/${row.id}`
    );
    return window.open(response.data.download_url, '_blank');
  };
  return downloadSensorRule();
};

export const actionFormatter = (row: AllSensorRuleFile) => {
  const DeleteButton = () => {
    const [, setRulesDeleteDialog] = useAtom(rulesDeleteDialogAtom);
    const [, setIsRulesDeleteDialogOpen] = useAtom(rulesDeleteDialogOpenAtom);

    const handleDelete = () => {
      setRulesDeleteDialog(row);
      setIsRulesDeleteDialogOpen(true);
    };

    return (
      <Button onClick={handleDelete}>
        <FontAwesomeIcon
          color="var(--mui-palette-text-secondary)"
          icon={DeleteIcon}
          size="lg"
        />
      </Button>
    );
  };

  const DownloadButton = () => {
    const handleDownload = () => {
      handleDownloadClick(row);
    };

    return (
      <Button onClick={handleDownload}>
        <FontAwesomeIcon
          color="var(--mui-palette-text-secondary)"
          icon={DownloadIcon}
          size="lg"
        />
      </Button>
    );
  };

  // if the file type is feed rule, we only show the download button (feed rules cannot be deleted)
  if (row.file_type === 'feed_rule') {
    return <DownloadButton />;
  }

  return (
    <Stack direction="row" spacing={{ xs: 2 }} sx={{ mr: { xs: 2, lg: 1 } }}>
      <DownloadButton />
      <CheckPermissions
        permissions={[PERMISSIONS.INTERNAL, PERMISSIONS.SENSOR_RULES_DELETE]}
      >
        <DeleteButton />
      </CheckPermissions>
    </Stack>
  );
};

export const fileNameFormatter = (field: string, handleClick: () => void) => (
  <ActionWrapper action={handleClick}>
    <Typography
      sx={{
        margin: 0,
        maxWidth: 400
      }}
      noWrap
    >
      {field}
    </Typography>
  </ActionWrapper>
);

export const fileTypeFormatter = (
  rowFileType: string,
  handleClick: () => void
) => {
  let fileIcon: IconDefinition;
  switch (rowFileType) {
    case 'configuration':
      fileIcon = ConfigurationIcon;
      break;
    case 'feed_rule':
      fileIcon = FeedRuleIcon;
      break;
    case 'rule':
      fileIcon = RuleIcon;
      break;
    default:
      fileIcon = FileIcon;
      break;
  }

  if (handleClick) {
    return (
      <ActionWrapper action={handleClick}>
        <Box sx={{ whiteSpace: 'nowrap' }}>
          <FontAwesomeIcon
            color="var(--mui-palette-text-secondary)"
            icon={fileIcon}
          />
          &nbsp;
          {startCase(rowFileType)}
        </Box>
      </ActionWrapper>
    );
  }
  return (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      <FontAwesomeIcon
        color="var(--mui-palette-text-secondary)"
        icon={fileIcon}
      />
      &nbsp;
      {startCase(rowFileType)}
    </Box>
  );
};

export const lastUpdatedFormatter = (
  rowLastUpdated: string,
  handleClick: () => void
) => {
  if (handleClick) {
    return (
      <ActionWrapper action={handleClick}>
        <ListItemText
          primary={`${dayjs.utc(rowLastUpdated).fromNow(true)} ago`}
          secondary={getDateTime(rowLastUpdated)}
          sx={{ margin: 0 }}
        />
      </ActionWrapper>
    );
  }
  return (
    <ListItemText
      primary={`${dayjs.utc(rowLastUpdated).fromNow(true)} ago`}
      secondary={getDateTime(rowLastUpdated)}
      sx={{ margin: 0 }}
    />
  );
};

export const levelFormatter = (
  rowLevel: SensorRuleLevelDTO,
  handleClick?: () => void
) => {
  if (handleClick) {
    return (
      <ActionWrapper action={handleClick}>
        <RulesLevelChip level={rowLevel} />
      </ActionWrapper>
    );
  }
  return <RulesLevelChip level={rowLevel} />;
};

export const standardFormatter = (field: string, handleClick: () => void) => {
  if (handleClick) {
    return (
      <ActionWrapper action={handleClick}>
        <ListItemText primary={field} sx={{ margin: 0 }} />
      </ActionWrapper>
    );
  }
  return <ListItemText primary={field} sx={{ margin: 0 }} />;
};

export { handleDownloadClick };
export default handleDownloadClick;

export const validateFile = (
  file: File | null,
  selectedType: '' | 'rule' | 'configuration',
  selectedOrganizationId: number,
  setError: UseFormSetError<RulesDialog>,
  clearErrors: UseFormClearErrors<RulesDialog>
) => {
  if (!file) return;

  const errors = [];
  const fileExtension = file.name.split('.').pop();

  if (
    (fileExtension === 'rules' && selectedType !== 'rule') ||
    (fileExtension === 'config' && selectedType !== 'configuration')
  ) {
    errors.push('File type selected does not match the given file.');
  }

  if (selectedType === 'rule') {
    if (selectedOrganizationId === 0 && file.name !== 'global.rules') {
      errors.push('File name should be "global.rules".');
    } else if (
      selectedOrganizationId !== 0 &&
      file.name !== 'organization.rules'
    ) {
      errors.push('File name should be "organization.rules".');
    }
  }

  if (
    selectedType === 'configuration' &&
    !['classification.config', 'threshold.config'].includes(file.name)
  ) {
    errors.push(
      'File name should be "classification.config" or "threshold.config".'
    );
  }

  if (errors.length > 0) {
    setError('attachments', {
      type: 'fileType',
      message: errors.join('\n')
    });
  } else {
    clearErrors('attachments');
  }
};
